import {
	type FetchCollectibleAuctionsFrontData,
	type FetchCollectibleAuctionsFrontParams,
	type FetchCollectibleAuctionsPartnerByPartnerIdData,
	type FetchCollectibleAuctionsPartnerByPartnerIdParams,
	type FetchCollectibleEventsData,
	type FetchCollectibleEventsParams,
	minutesToMs,
	type PaginationWithDetailedPaginationMode
} from '@capsicum/common';
import {
	type InfiniteData,
	useInfiniteQuery,
	type UseInfiniteQueryOptions,
	useQuery,
	type UseQueryOptions
} from '@tanstack/react-query';

import { collectiblesApi } from '../api';

export const UseCollectibleEventsQueryKeyBuilder = (params: FetchCollectibleEventsParams) =>
	['collectibles', 'auctions', params] as const;

export const UseCollectibleAuctionsFrontQueryKeyBuilder = (params: FetchCollectibleAuctionsFrontParams) =>
	['collectibles', 'auctions', 'front', params] as const;

export const UseCollectibleAuctionsTeamQueryKeyBuilder = (
	params: FetchCollectibleAuctionsPartnerByPartnerIdParams,
	options?: Omit<UseQueryOptions<FetchCollectibleAuctionsPartnerByPartnerIdData>, 'queryFn'>
) => ['collectibles', 'auctions', 'front', 'byTeam', params, options] as const;

export const useCollectibleEventsQueries = (
	params: FetchCollectibleEventsParams,
	options?: Omit<
		UseInfiniteQueryOptions<FetchCollectibleEventsData, Error, InfiniteData<FetchCollectibleEventsData>>,
		'queryFn' | 'queryKey'
	>
) => {
	return useInfiniteQuery({
		...options,
		enabled: !!params.mainCurrencyId && !!params.altCurrencyId,
		getNextPageParam: lastPage => {
			const metadata = lastPage.metadata as PaginationWithDetailedPaginationMode['metadata'];
			const currentPageCursor = metadata?.current_page || 1;
			const lastPageCursor = metadata?.last_page || 1;

			if (currentPageCursor < lastPageCursor) return currentPageCursor + 1;

			return undefined;
		},
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			return (await collectiblesApi).fetchCollectibleEvents({
				'details[]': [
					'collectibleImages',
					'attributeTranslations',
					'collectibleTranslations',
					'collectibleAttributeTranslations',
					'tagTranslations'
				],
				...params,
				page: pageParam as number | undefined
			});
		},
		queryKey: UseCollectibleEventsQueryKeyBuilder(params),
		staleTime: minutesToMs(5)
	});
};

export const useCollectibleAuctionsFrontQueries = (
	params: FetchCollectibleAuctionsFrontParams,
	options?: Omit<
		UseInfiniteQueryOptions<
			FetchCollectibleAuctionsFrontData,
			Error,
			InfiniteData<FetchCollectibleAuctionsFrontData>
		>,
		'queryFn' | 'queryKey'
	>
) => {
	return useInfiniteQuery({
		...options,
		enabled: !!params.mainCurrencyId && !!params.altCurrencyId,
		getNextPageParam: lastPage => {
			const metadata = lastPage.metadata as PaginationWithDetailedPaginationMode['metadata'];
			const currentPageCursor = metadata?.current_page || 1;
			const lastPageCursor = metadata?.last_page || 1;

			if (currentPageCursor < lastPageCursor) return currentPageCursor + 1;

			return undefined;
		},
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			return (await collectiblesApi).fetchCollectibleAuctionsFront({
				...params,
				page: pageParam as number | undefined
			});
		},
		queryKey: UseCollectibleAuctionsFrontQueryKeyBuilder(params),
		staleTime: minutesToMs(5)
	});
};

/**
 * Returns auctions for a specific partner by partnerId
 */

export const useCollectibleAuctionsTeamByPartnerIdFrontQueries = (
	params: FetchCollectibleAuctionsPartnerByPartnerIdParams,
	options?: Omit<UseQueryOptions<FetchCollectibleAuctionsPartnerByPartnerIdData>, 'queryFn'>
) => {
	return useQuery<FetchCollectibleAuctionsPartnerByPartnerIdData>({
		...options,
		enabled: !!params?.partnerId && !!params?.altCurrencyId && !!params?.mainCurrencyId,
		queryFn: async () => {
			return (await collectiblesApi).fetchCollectibleAuctionsPartnerByPartnerId({
				...params
			} as FetchCollectibleAuctionsPartnerByPartnerIdParams);
		},
		queryKey: UseCollectibleAuctionsTeamQueryKeyBuilder(params, options),
		select: data => data || {},
		staleTime: minutesToMs(5)
	});
};
